import { type JanusCtx, HandBack, Refusal } from './context.js';
import { TRIGGER_CONSENT_REASON } from './trigger-consent.js';
import { skipUnported, unportedSkipRow } from '../unported.js';
import { boundaryJournal, journalBoundaryContext } from './boundary-journal.js';

const rollbackReleases = new WeakMap<() => void, () => void>();
export function releaseRollback(restore: (() => void) | undefined): void { if (restore) rollbackReleases.get(restore)?.(); }

/** Read-only gates run first. A late failure retains only this effect's writes, never a context snapshot.
 * The callback must use its guarded context (including row aliases), so nested effects share the same journal. */
export function effectAttempt(ctx: JanusCtx, reason: string, resolve: (frame: JanusCtx) => void,
  { checkpoint = true, primary = false, previewOnly = false } = {}): boolean {
  ctx = journalBoundaryContext(ctx);
  const restore = checkpoint ? (ctx.dryRun ? previewRollback(ctx) : boundaryCheckpoint(ctx)) : undefined;
  try { resolve(ctx); return true; }
  catch (error) {
    restore?.();
    if (error instanceof HandBack && error.reason.includes(TRIGGER_CONSENT_REASON)) throw error;
    if (primary || !(error instanceof HandBack || error instanceof Refusal)) throw error;
    const detail = error instanceof HandBack ? error.reason : error.message;
    if (!previewOnly) ctx.rows.push(ctx.dryRun ? unportedSkipRow(`${reason}:${detail}`) : skipUnported(`${reason}:${detail}`));
    return false;
  } finally { releaseRollback(restore); }
}

/** Explicit checkpoints require a guarded context before callers retain mutable row references. */
export function previewRollback(ctx: JanusCtx): () => void {
  const journal = boundaryJournal(ctx);
  if (!journal) throw new Error('Boundary checkpoint requires journalBoundaryContext');
  rollbackReleases.set(journal.restore, journal.release);
  return journal.restore;
}

export function boundaryCheckpoint(ctx: JanusCtx): () => void {
  return previewRollback(ctx);
}
